import {useMemo, useState} from 'react'
import {InlineRow} from '@/components/shared/InlineRow'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {AlertTriangle, RefreshCw} from 'lucide-react'
import {useQueryClient} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {MutedText} from '@/components/shared/MutedText'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {StatValue} from '@/components/shared/StatValue'
import {Stack} from '@/components/shared/Stack'
import {StatusText} from '@/components/shared/StatusText'
import {Icon} from '@/components/shared/Icon'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {Card, CardContent} from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {Pagination} from '@/components/ui/pagination'
import {Skeleton} from '@/components/ui/skeleton'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {ErrorBanner} from '@/components/ui/error-banner'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {ErrorLogControllerAllEndpoint, ErrorLogControllerPatternsEndpoint} from '@/types/endpoint-contracts'
import {useErrorLogStats, useErrorLogList, useErrorLogErrorTypes} from '@/hooks/use-error-logs'
import {formatTimestamp, formatRelativeTime} from '@/lib/date-utils'
import {routes} from '@/lib/routes'
import {permissions} from '@/lib/permissions'
import {useAuth} from '@/contexts/auth-context'
import type {ErrorLog} from '@/types/background-jobs'
import {ErrorLogFilterParams} from '@/types/background-jobs'
import {PageRequestFields} from '@/types/page'
import {EmptyState} from '@/components/shared/EmptyState'
import {PageLayout, TabPanelContent} from '@/components/shared/PageLayout'

const PERIOD_OPTIONS = ['24h', '7d', '30d'] as const
const MIN_Q_LENGTH = 3
// URL-namespace prefixes for the per-tab UnifiedSearchBar instances —
// technical URL keys (`patterns.q`, `all.module`, …), not user-visible.
const PATTERNS_NAMESPACE = 'patterns'
const ALL_NAMESPACE = 'all'

export function ErrorLogs() {
    const i18n = useI18n()
    const services = useServices()
    usePageTitle(i18n.errorLogs.pageTitle)
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const {hasPermission} = useAuth()
    const [searchParams, setSearchParams] = useSearchParams()

    /* eslint-disable i18next/no-literal-string -- UI state params and default values, not entity fields */
    const activeTab = searchParams.get('tab') || 'patterns'
    const period = (searchParams.get(ErrorLogFilterParams.period) as typeof PERIOD_OPTIONS[number]) || '24h'
    /* eslint-enable i18next/no-literal-string */

    const patternsPagination = useServerPagination({
        endpoint: ErrorLogControllerPatternsEndpoint,
        defaultSortBy: ErrorLogControllerPatternsEndpoint.sort.LAST_SEEN_AT,
        defaultSortDir: SortDir.DESC,
        sortable: true,
        namespace: PATTERNS_NAMESPACE,
    })

    const allRecordsPagination = useServerPagination({
        endpoint: ErrorLogControllerAllEndpoint,
        defaultSortBy: ErrorLogControllerAllEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.DESC,
        sortable: true,
        namespace: ALL_NAMESPACE,
    })

    const [showPatternsMinQHint, setShowPatternsMinQHint] = useState(false)
    const [showAllMinQHint, setShowAllMinQHint] = useState(false)

    const {data: stats, isLoading: statsLoading, error: statsError} = useErrorLogStats(period)

    const {data: patternsData, isLoading: patternsLoading, error: patternsError} =
        useErrorLogErrorTypes(patternsPagination.queryString)
    const {data: allRecordsData, isLoading: allRecordsLoading, error: allRecordsError} =
        useErrorLogList(allRecordsPagination.queryString)

    const clearAllDialog = useDialog()

    const clearAllMutation = useEntityMutation({
        mutationFn: () => services.errorLogs.deleteAll(),
        invalidate: ['error-logs'],
        successMessage: i18n.errorLogs.clearAllSuccess,
        closeDialog: clearAllDialog.close,
    })

    const hasErrors = stats && (stats.totalPatterns > 0 || stats.totalOccurrences > 0)

    const isEmptyState = !statsLoading && !statsError && stats
        && stats.totalPatterns === 0 && stats.totalOccurrences === 0

    const handleTabChange = (tab: string) => {
        const newParams = new URLSearchParams(searchParams)
        newParams.set('tab', tab)
        newParams.set(PageRequestFields.page, '0')
        setSearchParams(newParams)
    }

    const handlePeriodChange = (value: string) => {
        const newParams = new URLSearchParams(searchParams)
        newParams.set(ErrorLogFilterParams.period, value)
        setSearchParams(newParams)
    }

    const handleModuleCardClick = (module: string) => {
        // Apply module filter to whichever tab is active.
        const target = activeTab === 'patterns' ? patternsPagination : allRecordsPagination
        target.setSearchAndFilters({filters: {...target.searchAndFilters.filters, MODULE: module}})
    }

    const handleTotalCardClick = () => {
        const target = activeTab === 'patterns' ? patternsPagination : allRecordsPagination
        const next = {...target.searchAndFilters.filters}
        delete next.MODULE
        target.setSearchAndFilters({filters: next})
    }

    const handleRefresh = () => {
        queryClient.invalidateQueries({queryKey: ['error-logs']})
    }

    const handlePatternRowClick = (record: ErrorLog) => {
        navigate(routes.admin.errorLogs.detail(record.errorLogId))
    }

    const handleAllRecordRowClick = (record: ErrorLog) => {
        navigate(routes.admin.errorLogs.detail(record.errorLogId))
    }

    const getPeriodLabel = (p: string) => {
        switch (p) {
            case '24h': return i18n.errorLogs.last24h
            case '7d': return i18n.errorLogs.last7d
            case '30d': return i18n.errorLogs.last30d
            default: return p
        }
    }

    const truncateMessage = (msg: string, maxLen: number = 80) => {
        if (msg.length <= maxLen) return msg
        return msg.substring(0, maxLen) + '...'
    }

    const statsSlot = statsLoading ? (
        <DetailGrid columns={4} responsive data-testid="stats-loading">
            {[1, 2, 3, 4].map(idx => (
                <Card key={idx}>
                    <CardContent className="pt-6">
                        <Stack gap="xs">
                            <Skeleton className="h-4 w-24"/>
                            <Skeleton className="h-8 w-16"/>
                        </Stack>
                    </CardContent>
                </Card>
            ))}
        </DetailGrid>
    ) : statsError ? (
        <ErrorBanner error={statsError as Error}/>
    ) : stats ? (
        <DetailGrid columns={4} responsive data-testid="stat-cards">
            {(() => {
                const entries = Object.entries(stats.countByModule)
                    .sort(([, a], [, b]) => b - a)
                const top = entries.slice(0, 5)
                const rest = entries.slice(5)
                const othersCount = rest.reduce((sum, [, c]) => sum + c, 0)
                return <>
                    {top.map(([module, count]) => (
                        <Card
                            key={module}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => handleModuleCardClick(module)}
                            data-testid={`stat-card-${module}`}
                        >
                            <CardContent className="pt-6">
                                <InlineRow justify="between">
                                    <div>
                                        <MutedText size="sm">{module}</MutedText>
                                        <StatValue value={count} variant={count > 0 ? 'danger' : 'default'}/>
                                    </div>
                                    <AlertTriangle className={count > 0 ? 'h-8 w-8 text-destructive' : 'h-8 w-8 text-muted-foreground'}/>
                                </InlineRow>
                            </CardContent>
                        </Card>
                    ))}
                    {othersCount > 0 && (
                        <Card
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={handleTotalCardClick}
                            data-testid="stat-card-others"
                        >
                            <CardContent className="pt-6">
                                <InlineRow justify="between">
                                    <div>
                                        <MutedText size="sm">{i18n.errorLogs.others({count: rest.length})}</MutedText>
                                        <StatValue value={othersCount} variant="danger"/>
                                    </div>
                                    <Icon icon={AlertTriangle} size="xl" className="text-destructive"/>
                                </InlineRow>
                            </CardContent>
                        </Card>
                    )}
                </>
            })()}
            <Card
                className="cursor-pointer hover:bg-muted/50"
                onClick={handleTotalCardClick}
                data-testid="stat-card-total"
            >
                <CardContent className="pt-6">
                    <InlineRow justify="between">
                        <div>
                            <MutedText size="sm">{i18n.errorLogs.totalFailures}</MutedText>
                            <StatValue value={stats.totalOccurrences}/>
                            <MutedText size="xs" className="mt-1">
                                {i18n.errorLogs.patternCount({count: stats.totalPatterns})}
                            </MutedText>
                        </div>
                        <Icon icon={AlertTriangle} size="xl" muted/>
                    </InlineRow>
                </CardContent>
            </Card>
        </DetailGrid>
    ) : null

    const dashboardFilters = (
        <>
            <Select value={period} onValueChange={handlePeriodChange}>
                <SelectTrigger className="w-[140px]" data-testid="filter-period">
                    <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                    {PERIOD_OPTIONS.map((p) => (
                        <SelectItem key={p} value={p}>{getPeriodLabel(p)}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                data-testid="btn-refresh"
            >
                <RefreshCw className="h-4 w-4"/>
            </Button>
        </>
    )

    const patternsTotalItems = patternsData?.totalElements || 0
    const patternsTotalPages = patternsData?.totalPages || 0
    const allRecordsTotalItems = allRecordsData?.totalElements || 0
    const allRecordsTotalPages = allRecordsData?.totalPages || 0

    const activePagination = useMemo(() => (
        activeTab === 'patterns'
            ? {totalItems: patternsTotalItems, totalPages: patternsTotalPages, ...patternsPagination}
            : {totalItems: allRecordsTotalItems, totalPages: allRecordsTotalPages, ...allRecordsPagination}
    ), [activeTab, patternsTotalItems, patternsTotalPages, allRecordsTotalItems, allRecordsTotalPages, patternsPagination, allRecordsPagination])

    return (
        <PageLayout
            variant="dashboard"
            icon={CONCEPT_ICON.errorLog}
            title={i18n.errorLogs.title as string}
            description={i18n.errorLogs.description as string}
            data-testid="system-failures-page"
            stats={statsSlot}
            count={activePagination.totalItems}
        >
            {isEmptyState && (
                <Card className="mb-6" data-testid="empty-state-card">
                    <CardContent className="pt-6">
                        <EmptyState
                            icon={AlertTriangle}
                            title={String(i18n.errorLogs.emptyState)}
                            description={String(i18n.errorLogs.emptyStateHint)}
                        />
                    </CardContent>
                </Card>
            )}

            <Card data-testid="error-logs-list">
                <CardContent className="pt-6">
                    <TabPanelContent
                        filters={dashboardFilters}
                        pagination={activePagination.totalItems > 0 ? (
                            <Pagination
                                page={activePagination.page}
                                pageSize={activePagination.pageSize}
                                totalItems={activePagination.totalItems}
                                totalPages={activePagination.totalPages}
                                onPageChange={activePagination.setPage}
                                onPageSizeChange={activePagination.setPageSize}
                                pageSizeOptions={activePagination.pageSizeOptions}
                            />
                        ) : undefined}
                    >
                        <Tabs value={activeTab} onValueChange={handleTabChange}>
                            <TabsList data-testid="tabs">
                                <TabsTrigger value="patterns" data-testid="tab-patterns">
                                    {i18n.errorLogs.tabPatterns}
                                    {patternsTotalItems > 0 && (
                                        <Badge
                                            data-testid="patterns-count"
                                            variant="count"
                                            className="ml-2"
                                        >
                                            {patternsTotalItems}
                                        </Badge>
                                    )}
                                </TabsTrigger>
                                <TabsTrigger value="all" data-testid="tab-all-records">
                                    {i18n.errorLogs.tabAllRecords}
                                    {allRecordsTotalItems > 0 && (
                                        <Badge
                                            data-testid="all-records-count"
                                            variant="count"
                                            className="ml-2"
                                        >
                                            {allRecordsTotalItems}
                                        </Badge>
                                    )}
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="patterns">
                                <Stack gap="sm">
                                    <UnifiedSearchBar
                                        contract={ErrorLogControllerPatternsEndpoint}
                                        value={patternsPagination.searchAndFilters}
                                        onChange={patternsPagination.setSearchAndFilters}
                                        placeholder={i18n.errorLogs.filterSearch as string}
                                        testIdNamespace="patterns"
                                        qMinChars={MIN_Q_LENGTH}
                                        onMinQHintChange={setShowPatternsMinQHint}
                                    />
                                    {showPatternsMinQHint && (
                                        <StatusText
                                            tone="warning"
                                            size="xs"
                                            data-testid="patterns-min-q-hint"
                                        >
                                            {i18n.errorLogs.minQHint}
                                        </StatusText>
                                    )}
                                    {patternsError ? (
                                        <ErrorBanner error={patternsError as Error}/>
                                    ) : (
                                        <Table data-testid="patterns-table">
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead data-testid="col-pattern-count" className="w-20">{i18n.errorLogs.columns.count}</TableHead>
                                                    <TableHead data-testid="col-pattern-module">{i18n.errorLogs.columns.module}</TableHead>
                                                    <TableHead data-testid="col-pattern-exception">{i18n.errorLogs.columns.exception}</TableHead>
                                                    <TableHead data-testid="col-pattern-message">{i18n.errorLogs.columns.message}</TableHead>
                                                    <TableHead data-testid="col-pattern-last-seen" className="w-36">{i18n.errorLogs.columns.lastSeen}</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {patternsLoading ? (
                                                    Array.from({length: 5}).map((_, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell><Skeleton className="h-4 w-12"/></TableCell>
                                                            <TableCell><Skeleton className="h-4 w-20"/></TableCell>
                                                            <TableCell><Skeleton className="h-4 w-32"/></TableCell>
                                                            <TableCell><Skeleton className="h-4 w-48"/></TableCell>
                                                            <TableCell><Skeleton className="h-4 w-20"/></TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : patternsData?.content.length === 0 ? (
                                                    <TableEmptyState
                                                        colSpan={5}
                                                        hasFilters={
                                                            patternsPagination.searchAndFilters.q.length > 0
                                                            || Object.keys(patternsPagination.searchAndFilters.filters).length > 0
                                                        }
                                                        matchMessage={i18n.errorLogs.noFailuresMatch as string}
                                                        emptyMessage={i18n.errorLogs.noPatterns as string}
                                                    />
                                                ) : (
                                                    patternsData?.content.map((record) => (
                                                        <TableRow
                                                            key={record.errorLogId}
                                                            className="cursor-pointer hover:bg-muted/50"
                                                            onClick={() => handlePatternRowClick(record)}
                                                            data-testid={`pattern-row-${record.errorLogId}`}
                                                        >
                                                            <TableCell>
                                                                <Badge
                                                                    variant={record.occurrenceCount > 100 ? 'destructive' : record.occurrenceCount > 10 ? 'warning' : 'outline'}
                                                                >
                                                                    {record.occurrenceCount.toLocaleString()}
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell className="">{record.sourceModule}</TableCell>
                                                            <TableCell className="font-mono text-sm">{record.exceptionType}</TableCell>
                                                            <TableCell className="max-w-[300px] truncate" title={record.message}>
                                                                {truncateMessage(record.message)}
                                                            </TableCell>
                                                            <TableCell className="text-sm text-muted-foreground" title={formatTimestamp(record.lastSeenAt)}>
                                                                {formatRelativeTime(record.lastSeenAt)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    )}
                                </Stack>
                            </TabsContent>

                            <TabsContent value="all">
                                <Stack gap="sm">
                                    <UnifiedSearchBar
                                        contract={ErrorLogControllerAllEndpoint}
                                        value={allRecordsPagination.searchAndFilters}
                                        onChange={allRecordsPagination.setSearchAndFilters}
                                        placeholder={i18n.errorLogs.filterSearch as string}
                                        testIdNamespace="all"
                                        qMinChars={MIN_Q_LENGTH}
                                        onMinQHintChange={setShowAllMinQHint}
                                    />
                                    {showAllMinQHint && (
                                        <StatusText
                                            tone="warning"
                                            size="xs"
                                            data-testid="all-min-q-hint"
                                        >
                                            {i18n.errorLogs.minQHint}
                                        </StatusText>
                                    )}
                                    {allRecordsError ? (
                                        <ErrorBanner error={allRecordsError as Error}/>
                                    ) : (
                                        <Table data-testid="all-records-table">
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead data-testid="col-all-type" className="w-24">{i18n.errorLogs.columns.type}</TableHead>
                                                    <TableHead data-testid="col-all-time" className="w-36">{i18n.errorLogs.columns.time}</TableHead>
                                                    <TableHead data-testid="col-all-module">{i18n.errorLogs.columns.module}</TableHead>
                                                    <TableHead data-testid="col-all-source-type">{i18n.errorLogs.columns.sourceType}</TableHead>
                                                    <TableHead data-testid="col-all-exception">{i18n.errorLogs.columns.exception}</TableHead>
                                                    <TableHead data-testid="col-all-message">{i18n.errorLogs.columns.message}</TableHead>
                                                    <TableHead data-testid="col-all-session-id">{i18n.errorLogs.columns.sessionId}</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {allRecordsLoading ? (
                                                    Array.from({length: 5}).map((_, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell><Skeleton className="h-4 w-16"/></TableCell>
                                                            <TableCell><Skeleton className="h-4 w-20"/></TableCell>
                                                            <TableCell><Skeleton className="h-4 w-20"/></TableCell>
                                                            <TableCell><Skeleton className="h-4 w-24"/></TableCell>
                                                            <TableCell><Skeleton className="h-4 w-32"/></TableCell>
                                                            <TableCell><Skeleton className="h-4 w-48"/></TableCell>
                                                            <TableCell><Skeleton className="h-4 w-24"/></TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : allRecordsData?.content.length === 0 ? (
                                                    <TableEmptyState
                                                        colSpan={7}
                                                        hasFilters={
                                                            allRecordsPagination.searchAndFilters.q.length > 0
                                                            || Object.keys(allRecordsPagination.searchAndFilters.filters).length > 0
                                                        }
                                                        matchMessage={i18n.errorLogs.noFailuresMatch as string}
                                                        emptyMessage={i18n.errorLogs.noFailures as string}
                                                    />
                                                ) : (
                                                    allRecordsData?.content.map((record) => (
                                                        <TableRow
                                                            key={record.errorLogId}
                                                            className="cursor-pointer hover:bg-muted/50"
                                                            onClick={() => handleAllRecordRowClick(record)}
                                                            data-testid={`record-row-${record.errorLogId}`}
                                                        >
                                                            <TableCell>
                                                                <Badge variant={record.isTemplate ? 'warning' : 'outline'}>
                                                                    {record.isTemplate ? i18n.errorLogs.badgePattern : i18n.errorLogs.badgeOccurrence}
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell className="text-sm text-muted-foreground" title={formatTimestamp(record.createdAt)}>
                                                                {formatRelativeTime(record.createdAt)}
                                                            </TableCell>
                                                            <TableCell className="">{record.sourceModule}</TableCell>
                                                            <TableCell className="text-sm">{record.sourceType}</TableCell>
                                                            <TableCell className="font-mono text-sm">{record.exceptionType}</TableCell>
                                                            <TableCell className="max-w-[300px] truncate" title={record.message}>
                                                                {truncateMessage(record.message, 120)}
                                                            </TableCell>
                                                            <TableCell>
                                                                {record.sessionId && (
                                                                    hasPermission(permissions.observability.view) ? (
                                                                        <a
                                                                            href={routes.admin.requestInspector.detail(record.sessionId)}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            data-testid={`session-chip-link-${record.errorLogId}`}
                                                                        >
                                                                            <Badge variant="outline" className="font-mono text-xs">
                                                                                {record.sessionId.substring(0, 20)}&hellip;
                                                                            </Badge>
                                                                        </a>
                                                                    ) : (
                                                                        <Badge variant="outline" className="font-mono text-xs" data-testid={`session-chip-text-${record.errorLogId}`}>
                                                                            {record.sessionId.substring(0, 20)}&hellip;
                                                                        </Badge>
                                                                    )
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    )}
                                </Stack>
                            </TabsContent>
                        </Tabs>
                    </TabPanelContent>
                </CardContent>
            </Card>

            {hasErrors && (
                <InlineRow justify="end" className="mt-4">
                    <Button
                        variant="link"
                        size="sm"
                        onClick={clearAllDialog.open}
                        data-testid="btn-clear-all"
                    >
                        {i18n.errorLogs.clearAll}
                    </Button>
                </InlineRow>
            )}

            <Dialog open={clearAllDialog.isOpen} onOpenChange={clearAllDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.errorLogs.clearAllConfirm}</DialogTitle>
                        <DialogDescription>
                            {i18n.errorLogs.clearAllDescription}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={clearAllDialog.close} data-testid="btn-cancel-clear-all">
                            {i18n.common.cancel}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => clearAllMutation.mutate()}
                            disabled={clearAllMutation.isPending}
                            data-testid="btn-confirm-clear-all"
                        >
                            {clearAllMutation.isPending ? i18n.common.deleting : i18n.common.delete}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageLayout>
    )
}
